"use client";

import { useCallback, useMemo } from "react";
import { Message } from "@/lib/core/crm/types";
import { Client } from "@/lib/types/database";
import { EmailRecipient } from "@/components/crm/messages/ClientEmailSelector";
import {
  buildInitialSubject,
  buildInitialBody,
  buildInitialRecipients,
  deriveInitialClient,
} from "@/lib/utils/crm/compose-modal-utils";

interface UseComposeMessageInitProps {
  isReplyMode: boolean;
  isForwardMode: boolean;
  replyMessage?: Message | null;
  replyingToMessage?: Message | null;
  forwardingMessage?: Message | null;
  modalData?: any;
  urlContactData?: { email?: string; name?: string } | null;
  propInitialRecipient?: string;
  propInitialClient?: Client;
  propInitialRecipients?: EmailRecipient[];
  propInitialSubject?: string;
  propInitialBody?: string;
}

/**
 * Hook for initializing compose message data
 * Handles reply/forward message initialization and recipient extraction
 */
export function useComposeMessageInit({
  isReplyMode,
  isForwardMode,
  replyMessage,
  replyingToMessage,
  forwardingMessage,
  modalData,
  urlContactData,
  propInitialRecipient,
  propInitialClient,
  propInitialRecipients,
  propInitialSubject,
  propInitialBody,
}: UseComposeMessageInitProps) {
  const modalInitialRecipients = Array.isArray(modalData?.initialRecipients)
    ? (modalData.initialRecipients as EmailRecipient[])
    : undefined;
  const effectiveInitialRecipients = propInitialRecipients ?? modalInitialRecipients;

  const originalMessage = useMemo(
    () => replyMessage || forwardingMessage || undefined,
    [replyMessage, forwardingMessage]
  );

  const replyEmail = useMemo(() => {
    if (isForwardMode) return undefined;
    if (effectiveInitialRecipients?.length) return undefined;

    // Reply-To wins over From — mailing-list / "via" mail sets From to the list
    // address, so replying to sender.email would target the group inbox instead
    // of the intended party. See extractReplyEmail in compose-modal-utils.
    return (
      replyMessage?.replyTo?.[0]?.email ||
      replyingToMessage?.replyTo?.[0]?.email ||
      replyMessage?.sender?.email ||
      replyingToMessage?.sender?.email ||
      (isReplyMode && replyMessage?.sender?.email) ||
      modalData?.customerEmail ||
      urlContactData?.email ||
      propInitialRecipient ||
      undefined
    );
  }, [
    isForwardMode,
    isReplyMode,
    replyMessage,
    replyingToMessage,
    modalData,
    urlContactData,
    propInitialRecipient,
    effectiveInitialRecipients,
  ]);

  const initialSubjectForForm = useMemo(
    () =>
      buildInitialSubject(isForwardMode, forwardingMessage || replyMessage) ||
      propInitialSubject,
    [isForwardMode, forwardingMessage, replyMessage, propInitialSubject]
  );

  const initialBodyForForm = useMemo(
    () =>
      buildInitialBody(isForwardMode, forwardingMessage || replyMessage) ||
      propInitialBody,
    [isForwardMode, forwardingMessage, replyMessage, propInitialBody]
  );

  const initialRecipientsForForm = useMemo<EmailRecipient[] | undefined>(
    () => {
      if (effectiveInitialRecipients?.length) {
        return effectiveInitialRecipients;
      }

      return buildInitialRecipients(
        isForwardMode,
        replyEmail,
        replyMessage,
        replyingToMessage,
        modalData?.customerName,
        urlContactData?.name,
        propInitialRecipients
      );
    },
    [
      isForwardMode,
      replyEmail,
      replyMessage,
      replyingToMessage,
      modalData?.customerName,
      urlContactData?.name,
      propInitialRecipients,
      effectiveInitialRecipients,
    ]
  );

  const initialClientForForm = useMemo(
    () => deriveInitialClient(propInitialClient, modalData),
    [propInitialClient, modalData]
  );

  // Addresses the opener wants pre-seeded as BCC (e.g. a support inbox's own
  // group address — see CompanySupportInboxDisplay.handleReply). Not related to
  // `effectiveInitialRecipients` (To); BCC is additive and independent of mode.
  const initialBccRecipientsForForm = useMemo<EmailRecipient[] | undefined>(() => {
    const bcc = modalData?.initialBcc;
    if (!Array.isArray(bcc) || bcc.length === 0) return undefined;
    return bcc
      .filter((email): email is string => typeof email === "string" && email.trim().length > 0)
      .map((email) => ({ id: `bcc-${email}`, type: "email" as const, email }));
  }, [modalData]);

  return useMemo(
    () => ({
      originalMessage,
      replyEmail,
      initialSubjectForForm,
      initialBodyForForm,
      initialRecipientsForForm,
      initialBccRecipientsForForm,
      initialClientForForm,
    }),
    [
      originalMessage,
      replyEmail,
      initialSubjectForForm,
      initialBodyForForm,
      initialRecipientsForForm,
      initialBccRecipientsForForm,
      initialClientForForm,
    ]
  );
}
